import * as THREE from 'three';
import { Rng } from '@/core/math/Rng';
import { GALACTIC_NORMAL } from './shaders/galaxyDefaults';
import { glslFloat } from './shaders/common.glsl';

/**
 * Background starfield and Milky-Way-style band.
 *
 * Drawn in the deep-space pass at a fixed radius with depth writes off, so it
 * never interacts with anything else's depth. Star colours follow a rough
 * blackbody distribution rather than being white points, and brightness follows
 * a power law so a handful of stars are genuinely bright and most are faint —
 * which is what makes a starfield read as depth rather than as noise.
 */

// --- Star distribution tunables ---------------------------------------------
/** Fraction of the star's displacement toward the plane that survives, biased per-star by BAND_CONCENTRATION_RANGE. */
const BAND_CONCENTRATION_BASE = 0.72;
const BAND_CONCENTRATION_RANGE = 0.2;
/** Skews the temperature draw toward cool (common) stars over hot (rare) ones. */
const TEMPERATURE_DRAW_POWER = 2.2;
const TEMPERATURE_MIN_KELVIN = 2600;
const TEMPERATURE_RANGE_KELVIN = 22000;
/** Power-law brightness: BRIGHTNESS_POWER >> 1 so most draws land near the floor and only a rare few reach the ceiling. */
const BRIGHTNESS_FLOOR = 0.12;
const BRIGHTNESS_POWER = 5.5;
const BRIGHTNESS_RANGE = 2.6;
const SIZE_FLOOR = 0.6;
const SIZE_POWER = 4;
const SIZE_RANGE = 3.4;
/** Bounding sphere margin over the true star radius, so no point gets frustum-culled at the edge. */
const BOUNDING_SPHERE_MARGIN = 1.1;
/** Point-sprite falloff power: shapes the soft round core vs. edge of each star point. */
const POINT_FALLOFF_POWER = 2.2;

// --- Atmospheric washout tunables (see setAtmosphericWashout) ---------------
// Extinction by the air column itself is mild. Earth's entire atmosphere costs
// a star about 0.2 magnitudes at the zenith — roughly 20% — not the two orders
// of magnitude a naive exp(-density * 3.2) once charged regardless of the hour.
const EXTINCTION_DENSITY_SCALE = 0.28;
/** How fast the daylight-washout term falls off with sun altitude (radians) once the sun is above the horizon. */
const DAYLIGHT_ALTITUDE_SCALE = 6;
/** Air density at which the daylight-washout term fully saturates. */
const AIR_SATURATION_SCALE = 1.5;

export class Starfield {
  readonly object: THREE.Points;
  private readonly geometry: THREE.BufferGeometry;
  readonly material: THREE.ShaderMaterial;

  constructor(count: number, seed = 0x51a4f1, radius = 100) {
    const rng = new Rng(seed);
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);

    // Galactic plane, tilted so it does not align with the ecliptic. Shared
    // with GalaxyBackground's baked band so the foreground points and the
    // background agree on where the Milky Way is.
    const planeNormal = new THREE.Vector3(...GALACTIC_NORMAL).normalize();

    for (let i = 0; i < count; i++) {
      // Half the stars are distributed uniformly, half concentrated near the
      // galactic plane, which gives a visible band without a texture.
      let dir: THREE.Vector3;
      if (i % 2 === 0) {
        const p = rng.onSphere();
        dir = new THREE.Vector3(p.x, p.y, p.z);
      } else {
        const p = rng.onSphere();
        dir = new THREE.Vector3(p.x, p.y, p.z);
        const along = dir.dot(planeNormal);
        dir.addScaledVector(planeNormal, -along * (BAND_CONCENTRATION_BASE + rng.next() * BAND_CONCENTRATION_RANGE)).normalize();
      }

      positions[i * 3] = dir.x * radius;
      positions[i * 3 + 1] = dir.y * radius;
      positions[i * 3 + 2] = dir.z * radius;

      // Blackbody-ish colour from a temperature draw biased toward cool stars.
      const t = Math.pow(rng.next(), TEMPERATURE_DRAW_POWER);
      const temperature = TEMPERATURE_MIN_KELVIN + t * TEMPERATURE_RANGE_KELVIN;
      const c = blackbodyColor(temperature);
      // Power-law brightness: many faint, few brilliant.
      const brightness = BRIGHTNESS_FLOOR + Math.pow(rng.next(), BRIGHTNESS_POWER) * BRIGHTNESS_RANGE;
      colors[i * 3] = c.r * brightness;
      colors[i * 3 + 1] = c.g * brightness;
      colors[i * 3 + 2] = c.b * brightness;
      sizes[i] = SIZE_FLOOR + Math.pow(rng.next(), SIZE_POWER) * SIZE_RANGE;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), radius * BOUNDING_SPHERE_MARGIN);

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uPixelRatio: { value: 1 },
        uBrightness: { value: 1 },
      },
      vertexShader: /* glsl */ `
        attribute float aSize;
        varying vec3 vColor;
        uniform float uPixelRatio;
        void main() {
          vColor = color;
          vec4 mv = viewMatrix * modelMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = aSize * uPixelRatio;
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec3 vColor;
        uniform float uBrightness;
        void main() {
          // Soft round point with a bright core, approximating a point spread
          // function rather than a hard square.
          vec2 d = gl_PointCoord - vec2(0.5);
          float r = length(d) * 2.0;
          if (r > 1.0) discard;
          float falloff = pow(1.0 - r, ${glslFloat(POINT_FALLOFF_POWER)});
          gl_FragColor = vec4(vColor * uBrightness * falloff, falloff);

          // See TerrainMaterial. The stars have to share the scene's colour
          // space and its exposure, or they drift relative to the baked galaxy
          // behind them - which does go through the renderer's own path - and
          // the band separates from the points in front of it.
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }
      `,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });

    this.object = new THREE.Points(this.geometry, this.material);
    this.object.frustumCulled = false;
    this.object.renderOrder = -1000;
  }

  setPixelRatio(ratio: number): void {
    this.material.uniforms.uPixelRatio.value = ratio;
  }

  /**
   * Stars are washed out by a bright atmosphere. Feeding in the local air
   * density is what makes them disappear at dawn and return in orbit, without
   * any scripted day/night switch.
   *
   * Daylight only washes anything out if there is air to scatter it. In vacuum
   * the sky stays black with the star overhead, which is why an astronaut in
   * daylight can still see stars and a pilot on a planet cannot. Scaling by sun
   * altitude alone would blank the sky in broad daylight on an airless moon.
   *
   * @returns the brightness factor, so the baked background can use the same one
   */
  setAtmosphericWashout(density: number, sunAltitude: number): number {
    // Extinction by the air column itself is mild. Earth's entire atmosphere
    // costs a star about 0.2 magnitudes at the zenith — roughly 20% — not the
    // two orders of magnitude that exp(-density * 3.2) charged. Worse, that
    // factor carried no notion of the hour, so on a thick-atmosphere world it
    // cut the stars to 2% at MIDNIGHT: Halcyon's night sky measured 100% pure
    // black, while thin-aired Kolyma kept its stars and so hid the bug.
    const extinction = Math.exp(-density * EXTINCTION_DENSITY_SCALE);
    // What actually hides stars in daylight is the sky being brighter than they
    // are, which is the sun-altitude term below. That one was already correct;
    // it was being multiplied by a permanent penalty that did its job again.
    const daylight = sunAltitude > 0 ? Math.exp(-sunAltitude * DAYLIGHT_ALTITUDE_SCALE) : 1;
    const air = Math.min(1, density * AIR_SATURATION_SCALE);
    const washout = Math.max(0, extinction * (1 - (1 - daylight) * air));
    this.material.uniforms.uBrightness.value = washout;
    return washout;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

/** Approximate linear-RGB colour of a blackbody at the given temperature. */
function blackbodyColor(kelvin: number): { r: number; g: number; b: number } {
  const t = kelvin / 100;
  let r: number;
  let g: number;
  let b: number;

  if (t <= 66) {
    r = 1;
    g = Math.min(1, Math.max(0, (99.4708025861 * Math.log(t) - 161.1195681661) / 255));
  } else {
    r = Math.min(1, Math.max(0, (329.698727446 * Math.pow(t - 60, -0.1332047592)) / 255));
    g = Math.min(1, Math.max(0, (288.1221695283 * Math.pow(t - 60, -0.0755148492)) / 255));
  }

  if (t >= 66) b = 1;
  else if (t <= 19) b = 0;
  else b = Math.min(1, Math.max(0, (138.5177312231 * Math.log(t - 10) - 305.0447927307) / 255));

  // Convert the sRGB-ish approximation to linear.
  const toLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return { r: toLinear(r), g: toLinear(g), b: toLinear(b) };
}
